import { showNotification } from "#/src/utils/notification";
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import axiosRateLimit from "#/src/utils/AxiosRateLimiter";
import * as logClient from "@classdojo/log-client";
import { isPrecanaryRequested } from "@web-monorepo/fetchers";

declare module "axios" {
  interface AxiosRequestConfig {
    /** The caller renders a local retry state instead of a global network toast. */
    silentNetworkError?: boolean;
  }
}

const fetchClient = ({ prefixPath }: { prefixPath: string }) => {
  const defaultOptions = {
    baseURL: prefixPath,
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "x-sign-attachment-urls": "true",
    },
    adapter: "fetch",
    withCredentials: true,
    credentials: "same-origin",
  };

  return axios.create(defaultOptions);
};

// Requests that must never surface a network-error toast: /logout (the user
// is leaving anyway) and the tutor activity heartbeat (a background ping that
// fires every minute — a toast would blame the user for something they never
// did; it self-corrects on the next tick).
const SILENT_NETWORK_ERROR_URLS = ["/logout", "/tutor/activity"];

const MAX_LOG_ARRAY_ITEMS = 10;
const MAX_LOG_OBJECT_KEYS = 25;
const SENSITIVE_LOG_KEY_PATTERN =
  /authorization|cookie|email|(?:first|last|display|full|parent|student|teacher|tutor|user)_?name|password|phone|secret|token|\bname\b/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function sanitizeForLog(value: unknown, depth = 0): unknown {
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (depth >= 3) {
    return "[truncated]";
  }

  if (Array.isArray(value)) {
    const result = value.slice(0, MAX_LOG_ARRAY_ITEMS).map((item) => sanitizeForLog(item, depth + 1));
    const remainingItems = value.length - MAX_LOG_ARRAY_ITEMS;
    return remainingItems > 0
      ? [...result, `[truncated ${remainingItems} more item${remainingItems === 1 ? "" : "s"}]`]
      : result;
  }

  if (!isRecord(value)) {
    return String(value);
  }

  const entries = Object.entries(value);
  const result = Object.fromEntries(
    entries
      .slice(0, MAX_LOG_OBJECT_KEYS)
      .map(([key, nestedValue]) => [
        key,
        SENSITIVE_LOG_KEY_PATTERN.test(key) ? "[redacted]" : sanitizeForLog(nestedValue, depth + 1),
      ]),
  );

  if (entries.length > MAX_LOG_OBJECT_KEYS) {
    const remainingKeys = entries.length - MAX_LOG_OBJECT_KEYS;
    result.__truncated = `${remainingKeys} more key${remainingKeys === 1 ? "" : "s"}`;
  }

  return result;
}

function getRequestPath(error: AxiosError) {
  const baseURL = error.config?.baseURL;
  const url = error.config?.url;
  if (!baseURL) return url;
  if (!url) return baseURL;
  return `${baseURL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

function getResponseErrorData(error: AxiosError) {
  const responseData = error.response?.data;
  if (isRecord(responseData) && "error" in responseData) {
    return responseData.error;
  }
  return responseData;
}

function logAxiosResponseError(error: AxiosError) {
  if (!error.response) return;

  void logClient.logMessage("ERROR", "marketplace.axios_request_failed", {
    site: "marketplace",
    statusCode: error.response.status,
    method: error.config?.method?.toUpperCase(),
    url: error.config?.url,
    baseURL: error.config?.baseURL,
    requestPath: getRequestPath(error),
    requestParams: sanitizeForLog(error.config?.params),
    responseError: sanitizeForLog(getResponseErrorData(error)),
    errorCode: error.code,
    errorName: error.name,
    errorMessage: error.message,
  });
}

export function errorHandler(error: AxiosError, notify: typeof showNotification = showNotification) {
  logAxiosResponseError(error);

  const isSilent = error.config?.silentNetworkError || SILENT_NETWORK_ERROR_URLS.includes(error.config?.url ?? "");
  if (error?.code === "ERR_NETWORK" && !isSilent) {
    notify({
      title: "Server is down.",
      message: "An error has occured. Please try again in a few minutes or you may send us a message.",
      color: "red",
    });
  } else if (error?.code === "ECONNABORTED" && !isSilent) {
    notify({
      title: "Network request aborted.",
      message: "An issue occurred while make a network request. Please try again in a few minutes.",
      color: "red",
    });
  }

  return Promise.reject(error);
}

export function addPrecanaryQueryParam(config: InternalAxiosRequestConfig) {
  if (isPrecanaryRequested()) {
    config.params = { ...config.params, usePrecanary: "true" };
  }
  return config;
}

const monolithApiClient = fetchClient({
  prefixPath: "/api/marketplace",
});

monolithApiClient.interceptors.request.use(addPrecanaryQueryParam);

axiosRateLimit(monolithApiClient, errorHandler);

const featureSwitchApiClient = fetchClient({
  prefixPath: "/api",
});

axiosRateLimit(featureSwitchApiClient, errorHandler);

export { featureSwitchApiClient, monolithApiClient };
